// ModelFileSurface — the product documents' host of the ONE viewing grammar
// (FileSurface): the peek section and the Manage drawer open every document
// here — a LOCAL file with its facts, notes and versions; a CANONICAL catalog
// artifact as a synthetic catalog row (ADR-068 §1: no files rows — the content
// address is the identity; FilePanel's `provenance: 'catalog'` face renders
// "Source · Product catalog", preview + download only).
//
// The host owns the lens (the pager walks the section's rows), the stage's link
// (`useStageLink` — one mint per open on both tiers, fresh at the moment it
// loads), the local file's detail read (notes + versions) and the note
// verbs (the comment convention — any reader). Write verbs arrive from the
// Manage drawer when it hosts the surface; the peek section passes none.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssetModelProjectItem } from '@ems-app/schemas/catalog/responses';
import { FileSurface } from '../../components/patterns/FileSurface.js';
import { catalogShaOf, displayName, isCatalogFileId, shortFileName, type LibraryFile } from '../../components/patterns/filesDisplay.js';
export { artifactFilename, catalogLibraryFile, familyOfMime } from '../../components/patterns/filesDisplay.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  downloadCatalogArtifact,
  downloadFile,
  fetchCatalogPreviewLink,
  fetchPreviewLink,
  useAddNote,
  useDeleteNote,
  useEditNote,
  useFileDetail,
  useRestoreNote,
  useStageLink,
} from '../../hooks/useFiles.js';
import { useMe } from '../../hooks/useMe.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';


export interface ModelFileSurfaceProps {
  projectId: string;
  model: Pick<AssetModelProjectItem, 'id' | 'name'>;
  /** The section's rows in render order — local files and catalog synthetics; the pager walks them. */
  items: LibraryFile[];
  openId: string;
  onOpen: (id: string | null) => void;
  /** The Manage drawer's write verbs (absent on the peek section — read acts only). */
  canWrite?: boolean;
  onRename?: (file: LibraryFile) => void;
  onMove?: (file: LibraryFile) => void;
  onReplace?: (file: LibraryFile) => void;
  onDelete?: (file: LibraryFile) => void;
}

export function ModelFileSurface({ projectId, model, items, openId, onOpen, canWrite = false, onRename, onMove, onReplace, onDelete }: ModelFileSurfaceProps) {
  const { t: tf } = useTranslation('files');
  // The screen's one zone (ADR-082 §2): the project's — a project screen.
  const display = useDisplayZone({ kind: 'project', projectId });
  const timeZone = display.zone ?? display.viewerZone ?? 'UTC';
  const toast = useToast();
  const me = useMe((s) => s.user.id).data ?? '';
  const fail = useRefusalToast('model-file');

  const index = items.findIndex((f) => f.id === openId);
  const base = index >= 0 ? items[index]! : null;
  const local = base !== null && !isCatalogFileId(base.id);

  // The local file's own read — notes + versions ride the surface.
  const detail = useFileDetail(projectId, local ? base.id : null);
  const file = useMemo<LibraryFile | null>(() => {
    if (!base) return null;
    if (!local || !detail.data || detail.data.id !== base.id) return base;
    return { ...base, notes: detail.data.notes, versions: detail.data.versions.map((v) => ({ ...v, thumb: null, fresh: false })) };
  }, [base, local, detail.data]);

  // The stage's link — one mint per open on both tiers (a local row, a catalog synthetic).
  const stage = useStageLink(projectId, base);

  const addNote = useAddNote(projectId);
  const editNote = useEditNote(projectId);
  const deleteNote = useDeleteNote(projectId);
  const restoreNote = useRestoreNote(projectId);

  if (!file) return null;
  const download = (f: LibraryFile) => {
    toast.info(tf('toast.downloading', { name: shortFileName(displayName(f)) }));
    if (isCatalogFileId(f.id)) downloadCatalogArtifact(projectId, catalogShaOf(f.id), f.original_name).catch(fail);
    else downloadFile(projectId, f).catch(fail);
  };
  const openInTab = (f: LibraryFile) => {
    const mint = isCatalogFileId(f.id) ? fetchCatalogPreviewLink(projectId, catalogShaOf(f.id), f.original_name) : fetchPreviewLink(projectId, f.id, 'original');
    mint.then((l) => window.open(l.url, '_blank', 'noopener'), fail);
  };
  const step = (delta: number) => {
    const next = items[Math.min(items.length - 1, Math.max(0, index + delta))];
    if (next && next.id !== openId) onOpen(next.id);
  };
  const writable = canWrite && local;

  return (
    <FileSurface
      file={file}
      anchorLabel={model.name}
      timeZone={timeZone}
      projectId={projectId}
      siteId={null}
      onClose={() => onOpen(null)}
      preview={stage.preview}
      onSourceError={() => stage.retry()}
      onSourceExpired={() => stage.refresh()}
      pager={
        items.length > 1 && index >= 0
          ? { onPrev: () => step(-1), onNext: () => step(1), prevDisabled: index <= 0, nextDisabled: index >= items.length - 1, position: { index: index + 1, total: items.length } }
          : undefined
      }
      onStep={items.length > 1 ? step : undefined}
      canWrite={writable}
      canReview={false}
      canNote={local}
      currentUserId={me}
      onDownload={download}
      onOpenInTab={(f) => (f.previewable ? openInTab(f) : download(f))}
      {...(writable ? { onRename, onMove, onReplace, onDelete } : {})}
      onAddNote={local ? (f, text) => addNote.mutateAsync({ fileId: f.id, body: text }).then(() => void toast.success(tf('toast.noteAdded'))) : undefined}
      onEditNote={local ? (f, noteId, text) => editNote.mutateAsync({ fileId: f.id, noteId, body: text }).then(() => void toast.success(tf('toast.noteSaved'))) : undefined}
      onDeleteNote={
        local
          ? (f, noteId) =>
              deleteNote.mutateAsync({ fileId: f.id, noteId }).then(
                () => void toast.success(tf('toast.noteDeleted'), { action: { label: tf('toast.undo'), onClick: () => void restoreNote.mutateAsync({ fileId: f.id, noteId }).catch(fail) } }),
                fail,
              )
          : undefined
      }
    />
  );
}
